import {
  completeQueryResult,
  hasCompleteQueryData,
  throwOnQueryError,
} from 'ZalamoLibrary/src/lib/core/graphql/complete-query-data';

import {
  BehaviorSubject,
  ReplaySubject,
  combineLatest,
  filter,
  first,
  map,
  of,
  pipe,
  pluck,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs';

import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  AvailableWallDecorScenesGQL,
  GetWallDecorsLookBookConfigForPrivateSessionGQL,
  GetWallDecorsLookBookConfigForPublicSessionGQL,
  WallDecorPieceForDisplayType,
  WallDecorSceneForDisplay,
} from 'ZalamoLibrary/src/lib/ui/look-book-front/wall-decor-front/graphql';

import { UntypedFormControl } from '@angular/forms';
import { ImagesContainer } from 'ZalamoLibrary/src/lib/upload/graphql';
import { LookBookPriceRounding } from '../../../../../../../photographer-panel/src/graphql';
import { Required } from 'ZalamoLibrary/src/lib/core/decorators/Required';
import { PhotoGroupList } from 'ZalamoLibrary/src/lib/ui/products/shopping-cart-back/actions/standalone';
import { groupArrayByIndex } from '../../../../../../../../helpers/array';
import { WallDecorRenderPreviewConfig } from '../../../../../../../photographer-client/src/app/modules/private-sessions/graphql';
import { RxjsOperators } from 'ZalamoLibrary/src/lib/core/operators/RxjsOperators';
import { ImagesInWallDecorMap } from '@ui/look-book-front/wall-decor-front/types';

@Component({
  selector: 'lib-wall-decor-scenes-list',
  templateUrl: './wall-decor-scenes-list.component.html',
  styleUrls: ['./wall-decor-scenes-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class WallDecorScenesListComponent implements OnInit {
  @Input() set limitPersonalization(value: boolean) {
    this._limitPersonalization.next(value);
  }

  @Input() lookBookProductConfigId = null;

  @Input() set imageRatio(value: number) {
    this._imageRatio.next(value);
  }

  get contextId(): string {
    return this._contextId;
  }

  @Input() set contextId(value: string) {
    this._contextId = value;
    this.contextId$.next(value);
  }

  context$ = new ReplaySubject<ImagesContainer>(1);
  contextId$ = new ReplaySubject<string>(1);

  config$ = combineLatest([this.context$, this.contextId$]).pipe(
    switchMap(([context, contextId]) => {
      switch (context) {
        case ImagesContainer.PrivateSession:
          return this.getWallDecorsLookBookConfigForPrivateSession
            .watch({
              variables: {
                privateSessionId: contextId,
              },
            })
            .valueChanges.pipe(
              throwOnQueryError,
              filter(hasCompleteQueryData),
              map((apolloQueryResult) => {
                const {
                  data: { getWallDecorsLookBookConfigForPrivateSession },
                } = completeQueryResult(apolloQueryResult);
                return getWallDecorsLookBookConfigForPrivateSession;
              }),
            );
        case ImagesContainer.PublicSession:
          return this.getWallDecorsLookBookConfigForPublicSessionGQL
            .watch({
              variables: {
                publicSessionId: contextId,
              },
            })
            .valueChanges.pipe(
              throwOnQueryError,
              tap((x) => console.log(x)),
              filter(hasCompleteQueryData),
              map((apolloQueryResult) => {
                const {
                  data: { getWallDecorLookBookConfigForPublicSession },
                } = completeQueryResult(apolloQueryResult);
                return getWallDecorLookBookConfigForPublicSession;
              }),
            );
      }
    }),
    tap((x) => console.log(x, this.rounding)),
  );
  scenes$ = combineLatest([
    this.availableWallDecorScenesGQL
      .fetch({})
      .pipe(pluck('data', 'getAvailableWallDecorScenes')),
    this.config$,
  ]).pipe(
    map(([scenes, config]) => {
      const labsInPlay = config.labProducts
        .filter((product) => String(product.type) === String(this.type))
        .map((product) => product.organizationId);
      return [...scenes]
        .filter(({ pieces, labs, wallDecorSceneForDisplayId }) => {
          return (
            labsInPlay.filter((lab) => labs.includes(lab)).length &&
            !!pieces.find(({ type }) => type === this.type)
          );
        })
        .sort((a, b) => a.pieces.length - b.pieces.length);
    }),
    tap((x) => console.log(x)),
  );
  @Input() type: WallDecorPieceForDisplayType =
    WallDecorPieceForDisplayType.Canvas;
  setCounts$ = this.scenes$.pipe(
    map((scenes) => {
      const options = new Set();
      scenes.forEach(({ pieces }) => options.add(pieces.length));
      return [...options];
    }),
  );
  @Output() save = new EventEmitter();
  @Output() pickedScene = new EventEmitter<WallDecorSceneForDisplay>();

  currentScene$ = this.pickedScene.pipe(shareReplay(1));
  currentScene: WallDecorSceneForDisplay;
  @Input() isUpdate = false;

  @Input() set context(context: ImagesContainer) {
    this.context$.next(context);
  }

  @Output() closed = new EventEmitter();

  @Input() set preconfig(scene: WallDecorRenderPreviewConfig) {
    console.log('SCENE FROM PRECONFIG', scene);
    if (!scene) {
      return;
    }
    this.scenes$
      .pipe(
        tap((x) => console.log(x)),
        tap((scenes: WallDecorSceneForDisplay[]) => {
          const matchingScene = scenes.find(
            ({ wallDecorSceneForDisplayId }) =>
              wallDecorSceneForDisplayId === scene.wallDecorSceneId,
          );
          this.currentCount.patchValue(matchingScene.pieces.length);
          of(null)
            .pipe(
              RxjsOperators.domElementExists(`#${scene.wallDecorSceneId}`),
              first(),
            )
            .subscribe(() => {
              this.pickedScene.next(matchingScene);
              this.currentScene = matchingScene;
              document
                .getElementById(matchingScene.wallDecorSceneForDisplayId)
                .scrollTo();
            });
        }),
        first(),
      )
      .subscribe((scenes) => {});
  }

  @Input() rounding: LookBookPriceRounding = LookBookPriceRounding.One;
  @Required @Input() preConfigValue: string;
  @Output() scenesCountChanged = new EventEmitter<number>();
  @HostBinding('class.isLocked') @Input() locked = false;
  @Input() availableImages: string[] = [];
  usedImagesList$ = new BehaviorSubject<string[]>([]);

  @Input() @Required set usedImages(images: ImagesInWallDecorMap) {
    if (images) {
      this.usedImagesList$.next(
        Object.values(images).map(({ photoId }) => photoId),
      );
    }
  }

  _limitPersonalization = new BehaviorSubject(false);
  private _imageRatio = new BehaviorSubject(1);
  @Input() @Required imageRelations: PhotoGroupList[] = [];
  @Output() preset = new EventEmitter<{
    matchingScene: WallDecorSceneForDisplay;
    pieces: Record<
      string,
      {
        WallDecorPieceForDisplayId: string;
        photoId: string;
      }
    >;
  }>();
  private _contextId: string;
  currentCount = new UntypedFormControl(1);
  visibleScenes$ = combineLatest([
    this.currentCount.valueChanges.pipe(startWith(this.currentCount.value)),
    this.scenes$,
    this._imageRatio,
    this._limitPersonalization,
  ]).pipe(
    map(([count, scenes, imageRatio, limitPersonalization]) => {
      if (this.availableImages.length) {
        const indexed = this.imageRelations.reduce(
          (previousValue, currentValue) => {
            return {
              ...previousValue,
              ...groupArrayByIndex(currentValue.photos, 'photoId'),
            };
          },
          {} as Record<
            string,
            {
              height: number;
              width: number;
              photoGroupHasPhotoId: string;
              photoId: string;
              photoGroupId: string;
            }
          >,
        );
        this.preset.next;
        // pickImagesForThumb(
        //   this.availableImages.map((photoId) => indexed[photoId]),
        //   scenes,
        //   count
        // )
        // ();
      }

      return scenes
        .filter(({ pieces }) => {
          const pieceRatio =
            (pieces[0].flip ? pieces[0].height : pieces[0].width) /
            (pieces[0].flip ? pieces[0].width : pieces[0].height);
          return (
            pieces.length === count &&
            (!limitPersonalization ||
              (Math.floor(pieceRatio) === Math.floor(imageRatio) &&
                Math.abs(pieceRatio - imageRatio) < 0.3))
          );
        })
        .sort(
          (a, b) =>
            b.pieces.reduce(
              (total, current) => total + current.width * current.height,
              0,
            ) -
            a.pieces.reduce(
              (total, current) => total + current.width * current.height,
              0,
            ),
        );
    }),
  );

  constructor(
    private availableWallDecorScenesGQL: AvailableWallDecorScenesGQL,
    private getWallDecorsLookBookConfigForPrivateSession: GetWallDecorsLookBookConfigForPrivateSessionGQL,
    private getWallDecorsLookBookConfigForPublicSessionGQL: GetWallDecorsLookBookConfigForPublicSessionGQL,
  ) {}

  ngOnInit(): void {}
}
